import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Sparkles, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PilotLandingPage from "@/pages/PilotLandingPage";

// Industry dropdown options map directly to Alfred variants.
const INDUSTRY_OPTIONS = [
  {
    key: "work",
    label: "Trade & Service",
    hint: "Automotive, plumbing, HVAC, electrical, contracting",
    accent: "#c9a24a",
    industry_label: "Trade & Service",
    services: [
      "Diagnostics",
      "Emergency Repairs",
      "Routine Service",
      "Estimates & Quotes",
      "Follow-up Care",
    ],
    tagline: "Straight talk. Real work. Local trust.",
    subheadline: "Describe what you need. We'll write it up before we show up.",
  },
  {
    key: "business_casual",
    label: "Salon, Retail & Small Business",
    hint: "Salon, restaurant, retail, consulting, marketing",
    accent: "#4a8fc9",
    industry_label: "Salon & Small Business",
    services: [
      "Consultation",
      "Bookings & Scheduling",
      "Ongoing Care",
      "Follow-up",
      "Custom Requests",
    ],
    tagline: "Modern service. On your schedule.",
    subheadline: "Tell us what you're looking for. We'll write it up before you arrive.",
  },
  {
    key: "business",
    label: "Professional Office",
    hint: "Legal, financial, medical, real estate",
    accent: "#8a7f5e",
    industry_label: "Professional Office",
    services: [
      "Initial Consultation",
      "Case / Matter Review",
      "Ongoing Representation",
      "Document Preparation",
      "Follow-up",
    ],
    tagline: "Discreet counsel. Modern access.",
    subheadline: "Describe your matter. We'll prepare a summary before your first call.",
  },
];

function buildDemoBusiness({ name, city, variantKey }) {
  const opt = INDUSTRY_OPTIONS.find((o) => o.key === variantKey) || INDUSTRY_OPTIONS[1];
  const displayName = name.trim() || "Your Business";
  const displayCity = city.trim();
  const now = new Date();
  const trialEnds = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  return {
    id: "demo-preview",
    slug: "demo-preview",
    name: displayName,
    industry: opt.industry_label,
    logo_url: null,
    city: displayCity || null,
    tagline: opt.tagline,
    hero_headline: displayCity
      ? `The ${opt.industry_label.toLowerCase()} ${displayCity} can rely on — now online.`
      : `${displayName} — now online.`,
    hero_subheadline: opt.subheadline,
    services: opt.services,
    phone: null,
    email: null,
    address: displayCity || null,
    accent_color: opt.accent,
    alfred_variant: opt.key,
    trial_started_at: now.toISOString(),
    trial_days: 30,
    trial_ends_at: trialEnds.toISOString(),
    trial_days_remaining: 30,
    active: true,
    created_at: now.toISOString(),
  };
}

const VALID_VARIANTS = new Set(["work", "business_casual", "business"]);

export default function DemoGenerator() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [variantKey, setVariantKey] = useState("");
  const [previewBiz, setPreviewBiz] = useState(null);
  const autoRanRef = useRef(false);

  // Read URL query params once on mount — prefill fields and auto-generate
  // if we have enough (business name + valid variant). Missing/invalid params
  // simply fall through to the manual form.
  useEffect(() => {
    if (autoRanRef.current) return;
    autoRanRef.current = true;

    const qpName = (searchParams.get("name") || "").trim();
    const qpCity = (searchParams.get("city") || "").trim();
    const qpVariant = (searchParams.get("variant") || "").trim().toLowerCase();

    if (qpName) setName(qpName);
    if (qpCity) setCity(qpCity);
    if (VALID_VARIANTS.has(qpVariant)) setVariantKey(qpVariant);

    // Auto-generate only when both required inputs are present + valid.
    if (qpName && VALID_VARIANTS.has(qpVariant)) {
      setPreviewBiz(
        buildDemoBusiness({ name: qpName, city: qpCity, variantKey: qpVariant })
      );
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, []);

  const handleGenerate = (e) => {
    e.preventDefault();
    if (!name.trim() || !variantKey) return;
    setPreviewBiz(buildDemoBusiness({ name, city, variantKey }));
    // Scroll to top so the demo starts clean on mobile.
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleReset = () => {
    setPreviewBiz(null);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleClearAll = () => {
    setPreviewBiz(null);
    setName("");
    setCity("");
    setVariantKey("");
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // Preview mode — render the real PilotLandingPage with a synthetic business
  // and inject a "New Demo" action into the demo ribbon.
  if (previewBiz) {
    return (
      <PilotLandingPage
        demoBusiness={previewBiz}
        demoMode
        demoActions={
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleReset}
              data-testid="demo-new-button"
              className="h-8 sm:h-9 px-3 sm:px-4 border border-white/25 text-white/80 hover:bg-white hover:text-black font-mono-plex text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-1.5 sm:gap-2 transition-colors"
            >
              <RefreshCw className="h-3 w-3" />
              <span className="hidden sm:inline">New Demo</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        }
      />
    );
  }

  // Form mode
  return (
    <main
      className="min-h-screen w-full bg-[#0a0a0b] text-white/90 relative overflow-hidden"
      data-testid="demo-generator-page"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(1000px 500px at 15% 10%, rgba(201,162,74,0.06), transparent 60%), radial-gradient(900px 500px at 85% 90%, rgba(74,143,201,0.05), transparent 60%)",
        }}
      />

      <div className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 md:px-12 py-10 md:py-16 min-h-screen flex flex-col">
        {/* Top nav */}
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/45 hover:text-white/90 inline-flex items-center gap-2 transition-colors"
            data-testid="demo-back-home"
          >
            <ArrowLeft className="h-3 w-3" /> OLS
          </Link>
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/40 flex items-center gap-2">
            <Sparkles className="h-3 w-3" style={{ color: "#c9a24a" }} />
            <span>Live Demo Generator</span>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 sm:mt-20"
        >
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
            For sales walkthroughs · No pilot is created
          </div>
          <h1
            className="font-serif-display text-white text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight"
            data-testid="demo-generator-title"
          >
            Show them
            <br />
            their storefront.
          </h1>
          <p className="mt-6 font-mono-plex text-sm text-white/50 leading-relaxed max-w-md">
            Type a business name and pick their industry. We&apos;ll render Alfred and their pilot
            page in real time — no signup, nothing saved.
          </p>

          <div className="mt-8 mb-10 h-px w-14" style={{ background: "#c9a24a80" }} />

          <form
            onSubmit={handleGenerate}
            className="space-y-6"
            data-testid="demo-generator-form"
            noValidate
          >
            <FieldLabel htmlFor="demo-business-name" required>
              Business name
            </FieldLabel>
            <Input
              id="demo-business-name"
              type="text"
              placeholder="e.g. Marion Auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              data-testid="demo-input-name"
              className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-base tracking-wide h-14 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50"
            />

            <FieldLabel htmlFor="demo-business-city">
              City <span className="text-white/25">(optional)</span>
            </FieldLabel>
            <Input
              id="demo-business-city"
              type="text"
              placeholder="e.g. Marion, OH"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              autoComplete="off"
              data-testid="demo-input-city"
              className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-base tracking-wide h-14 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50"
            />

            <FieldLabel required>Industry</FieldLabel>
            <div
              className="grid grid-cols-1 gap-3"
              data-testid="demo-industry-picker"
              role="radiogroup"
              aria-label="Industry"
            >
              {INDUSTRY_OPTIONS.map((opt) => {
                const selected = variantKey === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setVariantKey(opt.key)}
                    data-testid={`demo-industry-${opt.key}`}
                    className={`flex items-center gap-4 border p-4 text-left transition-colors min-h-[76px] ${
                      selected
                        ? "border-white/60 bg-white/[0.05]"
                        : "border-white/15 bg-white/[0.02] hover:border-white/30 active:border-white/50"
                    }`}
                  >
                    <img
                      src={`/alfred/${opt.key}.png`}
                      alt={opt.label}
                      className="h-14 w-14 rounded-full object-cover border shrink-0"
                      style={{
                        borderColor: selected ? opt.accent : "rgba(255,255,255,0.1)",
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-serif-display text-white text-lg leading-tight">
                        {opt.label}
                      </div>
                      <div className="mt-1 font-mono-plex text-[11px] text-white/45">
                        {opt.hint}
                      </div>
                    </div>
                    <div
                      className="h-4 w-4 rounded-full border shrink-0 flex items-center justify-center"
                      style={{
                        borderColor: selected ? opt.accent : "rgba(255,255,255,0.25)",
                      }}
                      aria-hidden
                    >
                      {selected && (
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: opt.accent }}
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="pt-4 flex items-center gap-4">
              <Button
                type="submit"
                disabled={!name.trim() || !variantKey}
                data-testid="demo-generate-button"
                className="rounded-none h-14 px-6 flex-1 bg-white text-black hover:bg-white/85 font-mono-plex text-[12px] uppercase tracking-[0.24em] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span>Generate Demo</span>
                <ArrowUpRight className="h-4 w-4" />
              </Button>
              {(name || city || variantKey) && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  data-testid="demo-clear-button"
                  className="h-14 px-4 font-mono-plex text-[11px] uppercase tracking-[0.24em] text-white/45 hover:text-white/85 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </motion.div>

        <div className="mt-16 pt-12 border-t border-white/5">
          <p className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/30">
            Transient preview — nothing is saved to the database.
          </p>
        </div>
      </div>
    </main>
  );
}

const FieldLabel = ({ children, htmlFor, required }) => (
  <label
    htmlFor={htmlFor}
    className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/50 block"
  >
    {children}
    {required && <span className="text-white/70 ml-1">*</span>}
  </label>
);
